import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Calendar, Check, CheckCircle2, Image as ImageIcon,
  RefreshCw, RotateCcw, SlidersHorizontal, X, Receipt, ChevronDown, ChevronRight, Boxes,
  FileText, ThumbsUp, FileSignature, Lock, ListChecks, Info,
} from "lucide-react";
import ConfirmationModal from "../../components/ConfirmationModal";
import PackageConfigurator from "./PackageConfigurator";
import StrategyReview from "./StrategyReview";
import StageChargesModal, { CHARGE_TYPES } from "./StageChargesModal";
import StageTasksChecklist from "./StageTasksChecklist";
import PurchaseOrderPanel from "./PurchaseOrderPanel";
import TenderPanel from "./TenderPanel";
import InteriorAgreementPanel from "./InteriorAgreementPanel";
import {
  getIdExecutionPlan, advancePlanStage, achievePlanStage, unskipPlanStage,
  refreshExecutionPlan, recordStageInstallment, getInstallmentReceiptPreview, getPackageBoq,
  sendStagePackageBoq, getStagePackageBoq,
} from "../../services/interiorExecution";

/**
 * BV associate's Interior Execution panel for one buyer lead (package architecture, v3_29).
 *
 * Flow: ① Configure Packages (include/scope/mode/anchor — the only data entry)
 *       ② Review Strategy (computed dates, grouped, warnings + installment amounts)
 *       ③ Plan board (mode-grouped stages, per-package sub-stage chain, payment gates,
 *          split prep/install legs for post-possession packages, achieve-with-photo).
 * Legacy plans (pre-package) render on the same board with the original 6-step chain.
 */

const LEGACY_SUB_STAGES = ["PLANNING", "PROCUREMENT", "DELIVERY", "EXECUTION", "QC", "CLOSURE"];

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  IN_PROGRESS: "bg-sky-50 text-sky-700 border-sky-200",
  COMPLETED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SKIPPED: "bg-slate-100 text-slate-500 border-slate-200",
};

const MODE_BADGE = {
  PRE_HANDOVER_MUST_FINISH_BEFORE_WBS: { label: "Before milestone", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  PRE_HANDOVER_START_AFTER_WBS: { label: "After milestone", cls: "bg-sky-50 text-sky-600 border-sky-200" },
  POST_HANDOVER: { label: "Post-possession", cls: "bg-violet-50 text-violet-600 border-violet-200" },
  DEFERRED: { label: "Deferred", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const GROUP_ORDER = [
  { modes: ["PRE_HANDOVER_MUST_FINISH_BEFORE_WBS"], title: "Before builder milestones" },
  { modes: ["PRE_HANDOVER_START_AFTER_WBS"], title: "After builder milestones" },
  { modes: ["POST_HANDOVER"], title: "Post-possession" },
  { modes: ["DEFERRED"], title: "Deferred" },
];

const SUBSTAGE_LABEL = {
  PLANNING: "Planning", PROCUREMENT: "Procurement", MANUFACTURING: "Manufacturing",
  DELIVERY: "Delivery", EXECUTION: "Execution", QC: "Quality Check", CLOSURE: "Closure",
};

// Charge-type display order + labels for the (per-package and consolidated) cost breakdown —
// sourced from the same canonical list the breakdown dropdown (StageChargesModal) uses, so every
// charge type the user can pick is ordered + labelled consistently here.
const CHARGE_ORDER = CHARGE_TYPES.map(([v]) => v);
const CHARGE_LABEL = Object.fromEntries(CHARGE_TYPES);
const chargeLabel = (t) =>
  CHARGE_LABEL[t] || t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// next-action hint shown on the collapsed row + as the step's accent. tone → chip classes.
const HINT_TONE = {
  flamingo: "bg-flamingo-50 text-flamingo-600 border-flamingo-200",
  emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
  amber: "bg-amber-50 text-amber-600 border-amber-200",
  slate: "bg-slate-50 text-slate-500 border-slate-200",
};

const ExecutionPlanPanel = ({ leadId, propertyId }) => {
  const [plan, setPlan] = useState(null); // active plan or null
  const [view, setView] = useState("loading"); // loading | configure | review | plan
  const [isRegenerate, setIsRegenerate] = useState(false);
  const [busy, setBusy] = useState(false);

  /* per-stage action modals */
  const [payStage, setPayStage] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", reference: "", date: todayISO() });
  // v3_33: rendered receipt HTML shown before recording; reset on any field change.
  const [receiptHtml, setReceiptHtml] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [achieveStage, setAchieveStage] = useState(null);
  const [achieveForm, setAchieveForm] = useState({ date: todayISO(), file: null });
  const [confirmUnskip, setConfirmUnskip] = useState(null);
  const [chargesStage, setChargesStage] = useState(null); // v3_34 breakdown modal
  // P1: per-package approved BOQ — { packageKey: group } + per-card open set
  const [packageBoq, setPackageBoq] = useState({});
  const [openBoq, setOpenBoq] = useState(() => new Set());
  // Plan board is a single-open accordion of compact package rows; openSections holds
  // per-package collapsed sub-sections ("<id>:checklist" / "<id>:details").
  const [openStageId, setOpenStageId] = useState(null);
  const [openSections, setOpenSections] = useState(() => new Set());
  const toggleSection = (key) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  // v3_51: per-package BOQ (buyer cost estimate, FYI) — per-stage generate/send + view modal
  const [boqBusyStage, setBoqBusyStage] = useState(null); // stageId currently generating, or null
  const [boqView, setBoqView] = useState(null); // this stage's ResponseConsolidatedBoq for the modal

  const load = useCallback(async () => {
    setView("loading");
    try {
      const p = await getIdExecutionPlan(leadId);
      setPlan(p);
      setView("plan");
      // Fetch the approved BOQ grouped per package (best-effort — cards still render without it).
      try {
        const boq = await getPackageBoq(leadId);
        const byKey = {};
        (boq.groups || []).forEach((g) => { byKey[g.packageKey] = g; });
        setPackageBoq(byKey);
      } catch (boqErr) { setPackageBoq({}); }
    } catch (e) {
      setPlan(null);
      setView("configure");
    }
  }, [leadId]);

  const toggleBoq = (key) =>
    setOpenBoq((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  useEffect(() => { load(); }, [load]);

  /* ---------- lifecycle actions ---------- */
  const doAdvance = async (stage) => {
    setBusy(true);
    try { setPlan(await advancePlanStage(stage.idExecutionPlanStageId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot advance stage"); }
    finally { setBusy(false); }
  };

  const updatePayForm = (patch) => {
    setPayForm((prev) => ({ ...prev, ...patch }));
    setReceiptHtml(null); // any edit invalidates the previewed receipt
  };

  const loadReceiptPreview = async () => {
    if (!payForm.amount || !payForm.reference.trim()) return toast.error("Amount and reference are required");
    setPreviewBusy(true);
    try {
      const fmt = new Date(payForm.date).toLocaleDateString("en-GB").replace(/\//g, "-"); // dd-mm-yyyy
      const res = await getInstallmentReceiptPreview(payStage.idExecutionPlanStageId, {
        amount: payForm.amount,
        reference: payForm.reference.trim(),
        paymentDate: fmt,
      });
      setReceiptHtml(res.html);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to render the receipt preview");
    } finally {
      setPreviewBusy(false);
    }
  };

  const submitPayment = async () => {
    if (!payForm.amount || !payForm.reference.trim()) return toast.error("Amount and reference are required");
    if (!receiptHtml) return toast.error("Preview the receipt before recording");
    setBusy(true);
    try {
      await recordStageInstallment(
        leadId, payStage.idExecutionPlanStageId, Number(payForm.amount),
        payForm.reference.trim(), `${payForm.date}T00:00:00`
      );
      toast.success("Installment recorded — receipt sent to the buyer, procurement unlocked");
      setPayStage(null);
      setReceiptHtml(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record payment");
    } finally { setBusy(false); }
  };

  const submitAchieve = async () => {
    if (!achieveForm.date) return toast.error("Achieved date is required");
    setBusy(true);
    try {
      setPlan(await achievePlanStage(achieveStage.idExecutionPlanStageId, achieveForm.date, achieveForm.file));
      toast.success("Stage marked achieved");
      setAchieveStage(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record achievement");
    } finally { setBusy(false); }
  };

  const doUnskip = async () => {
    const stage = confirmUnskip; setConfirmUnskip(null); setBusy(true);
    try {
      setPlan(await unskipPlanStage(stage.idExecutionPlanStageId));
      toast.success("Stage revived — record the installment before its lock date to keep it alive");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to unskip"); }
    finally { setBusy(false); }
  };

  const doRefresh = async () => {
    setBusy(true);
    try { setPlan(await refreshExecutionPlan(leadId)); toast.success("Plan re-synced with builder WBS targets"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to refresh"); }
    finally { setBusy(false); }
  };

  /* ---------- per-package BOQ (buyer cost estimate, FYI, v3_51) ---------- */
  const sendStageBoq = async (stageId) => {
    setBoqBusyStage(stageId);
    try {
      await sendStagePackageBoq(stageId);
      toast.success("Cost estimate generated & shared with the buyer");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate the cost estimate");
    } finally { setBoqBusyStage(null); }
  };

  const openStageBoqView = async (stageId) => {
    setBoqBusyStage(stageId);
    try { setBoqView(await getStagePackageBoq(stageId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load the cost estimate"); }
    finally { setBoqBusyStage(null); }
  };

  /* ---------- step header (wizard) ---------- */
  const StepHeader = ({ active }) => (
    <div className="flex items-center gap-2 mb-4">
      {["Configure Packages", "Review Strategy", "Execution Plan"].map((label, i) => {
        const key = ["configure", "review", "plan"][i];
        const isActive = key === active;
        const isDone = ["configure", "review", "plan"].indexOf(active) > i;
        return (
          <React.Fragment key={key}>
            {i > 0 && <div className="h-px w-6 bg-slate-200" />}
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
              isActive ? "bg-flamingo-50 text-flamingo-600 border-flamingo-200"
                : isDone ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                  : "bg-slate-50 text-slate-400 border-slate-200"
            }`}>
              {isDone ? <Check size={11} /> : <span>{i + 1}</span>} {label}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );

  /* ---------- renders ---------- */
  if (view === "loading") {
    return <div className="space-y-2 p-1">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-slate-100 animate-pulse" />)}</div>;
  }

  if (view === "configure") {
    return (
      <div>
        <StepHeader active="configure" />
        <PackageConfigurator leadId={leadId} onReview={() => setView("review")} />
      </div>
    );
  }

  if (view === "review") {
    return (
      <div>
        <StepHeader active="review" />
        <StrategyReview
          leadId={leadId}
          isRegenerate={isRegenerate || !!plan}
          onBack={() => setView("configure")}
          onGenerated={(p) => { setPlan(p); setIsRegenerate(false); setView("plan"); }}
        />
      </div>
    );
  }

  /* Plan board */
  const isPackagePlan = plan.stages.some((s) => s.executionMode);
  // v3_51: per-package BOQ is FYI (no approval). A package's cost estimate is generated + sent per
  // stage; the buyer's payment (which requires the BOQ to be SENT) is what unlocks procurement.
  const anyBoqSent = plan.stages.some((s) => s.boqStatus === "SENT");
  const groups = isPackagePlan
    ? [
        ...GROUP_ORDER.map((g) => ({ ...g, stages: plan.stages.filter((s) => g.modes.includes(s.executionMode)) })),
        { title: null, stages: plan.stages.filter((s) => !s.executionMode) },
      ].filter((g) => g.stages.length > 0)
    : [{ title: null, stages: plan.stages }];

  // Consolidated breakdown across the whole execution — aggregate every (non-skipped) package's
  // charge lines by type + a grand total, so the user sees the full cost picture up-front instead
  // of adding up per-package breakdowns.
  const consolidated = (() => {
    const active = plan.stages.filter((s) => s.status !== "SKIPPED");
    let grandTotal = 0;
    let pricedCount = 0;
    const catMap = {};
    active.forEach((s) => {
      if (s.installmentAmount != null) {
        pricedCount += 1;
        grandTotal += Number(s.installmentAmount) || 0;
      }
      (s.charges || []).forEach((c) => {
        catMap[c.chargeType] = (catMap[c.chargeType] || 0) + (Number(c.amount) || 0);
      });
    });
    const rank = (t) => {
      const i = CHARGE_ORDER.indexOf(t);
      return i < 0 ? 999 : i;
    };
    const categories = Object.entries(catMap)
      .map(([type, amount]) => ({ type, amount }))
      .sort((a, b) => rank(a.type) - rank(b.type));
    return { totalCount: active.length, pricedCount, grandTotal, categories, unpriced: active.length - pricedCount };
  })();

  return (
    <div>
      <StepHeader active="plan" />
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">Interior Execution Plan</p>
          <p className="text-xs text-slate-500">
            Generated {fmt(plan.generatedAt)} · payment unlocks each package's procurement
            {isPackagePlan ? " · pre-handover packages auto-skip when the lock passes unpaid" : " · stages auto-skip when the lock date passes unpaid"}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={doRefresh} disabled={busy} title="Re-sync with builder WBS targets"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 text-slate-600 text-xs font-semibold px-3 py-2 hover:bg-slate-50 disabled:opacity-60">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={() => { setIsRegenerate(true); setView("configure"); }} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-xl border border-flamingo-200 text-flamingo-600 text-xs font-semibold px-3 py-2 hover:bg-flamingo-50 disabled:opacity-60">
            <SlidersHorizontal size={13} /> Reconfigure
          </button>
        </div>
      </div>

      {consolidated.grandTotal > 0 && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-flamingo-50/60 to-white p-4">
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <Receipt size={16} className="text-flamingo-600" />
              <p className="text-sm font-semibold text-slate-900">Consolidated Cost Breakdown</p>
            </div>
            <span className="text-xs text-slate-500">
              {consolidated.pricedCount} of {consolidated.totalCount} package{consolidated.totalCount === 1 ? "" : "s"} priced
            </span>
          </div>
          {consolidated.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {consolidated.categories.map((c) => (
                <div key={c.type} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 min-w-[92px]">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">{chargeLabel(c.type)}</p>
                  <p className="text-sm font-semibold text-slate-800 tabular-nums">{fmtINR(c.amount)}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 pt-2.5">
            <span className="text-sm font-semibold text-slate-700">Total execution cost</span>
            <span className="text-lg font-bold text-slate-900 tabular-nums">{fmtINR(consolidated.grandTotal)}</span>
          </div>
          {consolidated.unpriced > 0 && (
            <p className="text-[11px] text-slate-400 mt-1.5">
              {consolidated.unpriced} package{consolidated.unpriced === 1 ? "" : "s"} not yet priced — the total grows as each package's tender is awarded.
            </p>
          )}
        </div>
      )}

      <InteriorAgreementPanel leadId={leadId} boqReady={anyBoqSent} />

      <div className="space-y-4">
        {groups.map((group, gi) => (
          <div key={group.title || `g${gi}`}>
            {group.title && (
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{group.title}</p>
            )}
            <div className="space-y-2">
              {group.stages.map((s) => renderStageRow(s))}
            </div>
          </div>
        ))}
      </div>

      {/* payment modal — fill → preview receipt → record & send to buyer (v3_33) */}
      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${payStage ? "" : "hidden"}`}>
        <div className={`bg-white rounded-2xl shadow-xl w-full ${receiptHtml ? "max-w-3xl" : "max-w-md"} max-h-[90vh] flex flex-col`}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">Record Installment — {payStage?.stageLabel || payStage?.stageKey}</h3>
            <button onClick={() => { setPayStage(null); setReceiptHtml(null); }} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="p-5 space-y-3 overflow-y-auto">
            <p className="text-xs text-slate-500">Due by <span className="font-semibold">{fmt(payStage?.designLockDate)}</span> · planned amount {fmtINR(payStage?.installmentAmount)}</p>
            {payStage?.referralRewardCredit > 0 && (
              <p className="text-xs text-emerald-600">
                Referral reward −{fmtINR(payStage.referralRewardCredit)} applied · net payable {fmtINR(payStage.netInstallmentAmount)}
              </p>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Amount (₹) <span className="text-flamingo-600">*</span></label>
                <input type="number" value={payForm.amount} onChange={(e) => updatePayForm({ amount: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Reference / UTR <span className="text-flamingo-600">*</span></label>
                <input value={payForm.reference} onChange={(e) => updatePayForm({ reference: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Payment Date</label>
                <input type="date" value={payForm.date} max={todayISO()} onChange={(e) => updatePayForm({ date: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40" />
              </div>
            </div>

            {/* receipt preview — the exact letterhead body that will be signed + emailed */}
            {receiptHtml ? (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Receipt preview — sent to the buyer on record</p>
                <iframe
                  title="Receipt preview"
                  srcDoc={receiptHtml}
                  sandbox=""
                  className="w-full h-[420px] rounded-xl border border-slate-200 bg-white"
                />
              </div>
            ) : (
              <p className="text-xs text-slate-400">Preview the receipt to enable recording — it is emailed to the unit buyer the moment you record.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button onClick={() => { setPayStage(null); setReceiptHtml(null); }} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
            <button onClick={loadReceiptPreview} disabled={previewBusy}
              className="rounded-xl border border-flamingo-200 text-flamingo-600 hover:bg-flamingo-50 disabled:opacity-60 text-sm font-semibold px-4 py-2.5">
              {previewBusy ? "Rendering…" : receiptHtml ? "Refresh Preview" : "Preview Receipt"}
            </button>
            <button onClick={submitPayment} disabled={busy || !receiptHtml}
              title={!receiptHtml ? "Preview the receipt first" : ""}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5">
              {busy ? "Saving…" : "Record & Unlock"}
            </button>
          </div>
        </div>
      </div>

      {/* achieve modal */}
      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${achieveStage ? "" : "hidden"}`}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">Mark Complete — {achieveStage?.stageLabel || achieveStage?.stageKey}</h3>
            <button onClick={() => setAchieveStage(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="p-5 space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Achieved Date <span className="text-flamingo-600">*</span></label>
              <input type="date" value={achieveForm.date} max={todayISO()} onChange={(e) => setAchieveForm({ ...achieveForm, date: e.target.value })}
                className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flamingo-500/40" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Completion Photo (optional)</label>
              <input type="file" accept="image/*" onChange={(e) => setAchieveForm({ ...achieveForm, file: e.target.files?.[0] || null })}
                className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-slate-700 hover:file:bg-slate-200" />
            </div>
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button onClick={() => setAchieveStage(null)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
            <button onClick={submitAchieve} disabled={busy} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5">{busy ? "Saving…" : "Mark Complete"}</button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirmUnskip}
        onConfirm={doUnskip}
        onCancel={() => setConfirmUnskip(null)}
        message={`Revive "${confirmUnskip?.stageLabel || confirmUnskip?.stageKey}"? Its due date resets to today's math — record the installment promptly or it will auto-skip again.`}
      />

      {chargesStage && (
        <StageChargesModal
          stage={chargesStage}
          onClose={() => setChargesStage(null)}
          onSaved={load}
        />
      )}

      {/* consolidated BOQ — aggregated view (what the buyer reviews) */}
      {boqView && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Consolidated Cost Estimate</h3>
                <p className="text-xs text-slate-500">
                  {boqView.unitNo ? `Unit ${boqView.unitNo}` : ""}{boqView.projectName ? ` · ${boqView.projectName}` : ""}
                  {boqView.phaseName ? ` — ${boqView.phaseName}` : ""}
                </p>
              </div>
              <button onClick={() => setBoqView(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {(boqView.packages || []).map((pk) => (
                    <React.Fragment key={pk.planStageId}>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 font-semibold text-slate-800" colSpan={2}>
                          {pk.label}
                          {pk.isDeferred && !pk.isPriced && (
                            <span className="ml-2 text-[11px] font-medium text-slate-400">to be priced later</span>
                          )}
                        </td>
                      </tr>
                      {pk.lines.map((ln, i) => (
                        <tr key={i} className="text-slate-600">
                          <td className="py-1 pl-4">{ln.description?.trim() || ln.label}</td>
                          <td className="py-1 text-right tabular-nums">{fmtINR(ln.amount)}</td>
                        </tr>
                      ))}
                      {pk.isPriced && (
                        <tr className="font-medium text-slate-700">
                          <td className="py-1.5 pl-4">{pk.label} total</td>
                          <td className="py-1.5 text-right tabular-nums">{fmtINR(pk.total)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200 text-slate-600">
                    <td className="py-1.5 text-right">Subtotal</td>
                    <td className="py-1.5 text-right tabular-nums">{fmtINR(boqView.subtotal)}</td>
                  </tr>
                  <tr className="text-slate-600">
                    <td className="py-1 text-right">GST</td>
                    <td className="py-1 text-right tabular-nums">{fmtINR(boqView.gstTotal)}</td>
                  </tr>
                  <tr className="font-bold text-slate-900 text-base">
                    <td className="py-2 text-right">Grand Total</td>
                    <td className="py-2 text-right tabular-nums">{fmtINR(boqView.grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
              {boqView.pdfUrl && (
                <a href={boqView.pdfUrl} target="_blank" rel="noopener noreferrer"
                  className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white inline-flex items-center gap-1.5">
                  <FileText size={14} /> Signed PDF
                </a>
              )}
              <button onClick={() => setBoqView(null)} className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-5 py-2.5">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  /* Per-stage cost-estimate (BOQ) control — FYI to the buyer, no approval (v3_51). Rendered inside
     each package row's action zone. Priced → generate & send; sent → view + regenerate. */
  function renderStageBoq(s) {
    const priced = s.installmentAmount != null;
    const sent = s.boqStatus === "SENT";
    const busyThis = boqBusyStage === s.idExecutionPlanStageId;
    return (
      <div className={`rounded-xl border p-3 ${sent ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="flex items-start gap-2 flex-wrap">
          {sent ? <ThumbsUp size={15} className="mt-0.5 text-emerald-600" /> : <FileText size={15} className="mt-0.5 text-amber-600" />}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-800">Buyer cost estimate (BOQ)</p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {sent
                ? `Shared with the buyer${s.boqSentAt ? ` on ${fmt(s.boqSentAt)}` : ""} (FYI). The buyer's package payment unlocks procurement.`
                : priced
                  ? "This package is priced — generate the estimate and share it with the buyer, then collect the payment."
                  : "Set this package's breakdown before generating the cost estimate."}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {sent && (
              <button onClick={() => openStageBoqView(s.idExecutionPlanStageId)} disabled={busyThis}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white text-slate-600 text-xs font-semibold px-3 py-2 hover:bg-slate-50 disabled:opacity-60">
                <FileSignature size={13} /> View estimate
              </button>
            )}
            <button onClick={() => sendStageBoq(s.idExecutionPlanStageId)} disabled={busyThis || !priced}
              title={!priced ? "Set the package breakdown first" : ""}
              className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2 disabled:opacity-40">
              <FileSignature size={13} /> {busyThis ? "Working…" : sent ? "Regenerate & resend" : "Generate & send"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* short "what to do next" hint for the collapsed row + step accent */
  function nextActionHint(s) {
    if (s.status === "SKIPPED") return { label: "Unskip", tone: "amber" };
    if (s.status === "COMPLETED") return { label: "Completed", tone: "emerald" };
    if (s.executionMode === "DEFERRED" && !s.executionStartDate) return { label: "Deferred", tone: "slate" };
    switch (s.subStage) {
      case "PLANNING":
        if (!s.hasAwardedTender) return { label: "Float & award tender", tone: "flamingo" };
        if (s.installmentAmount == null) return { label: "Set breakdown", tone: "flamingo" };
        if (s.boqStatus !== "SENT") return { label: "Send cost estimate", tone: "flamingo" };
        if (!s.paymentUnlocked) return { label: "Record installment", tone: "emerald" };
        return { label: "Start procurement", tone: "slate" };
      case "PROCUREMENT": return { label: "Send / track PO", tone: "flamingo" };
      case "MANUFACTURING": return { label: "Production checks", tone: "slate" };
      case "DELIVERY": return { label: "Confirm delivery", tone: "slate" };
      case "EXECUTION": return { label: "Mark work done", tone: "slate" };
      case "QC": return { label: "Buyer walkthrough", tone: "slate" };
      case "CLOSURE": return { label: "Mark complete", tone: "emerald" };
      default: return null;
    }
  }

  /* one collapsed package row → expands into the current-step action zone */
  function renderStageRow(s) {
    const id = s.idExecutionPlanStageId;
    const isSkipped = s.status === "SKIPPED";
    const isDone = s.status === "COMPLETED";
    const subStages = s.substageProfile?.length ? s.substageProfile : LEGACY_SUB_STAGES;
    const subIdx = subStages.indexOf(s.subStage);
    const mode = MODE_BADGE[s.executionMode];
    const expanded = openStageId === id;
    const pct = isDone ? 100 : isSkipped ? 0 : Math.round(((subIdx < 0 ? 0 : subIdx) / Math.max(subStages.length - 1, 1)) * 100);
    const hint = nextActionHint(s);

    return (
      <div key={id} className={`rounded-2xl border bg-white ${expanded ? "border-flamingo-200 shadow-sm" : "border-slate-200"} ${isSkipped ? "opacity-75" : ""}`}>
        <button onClick={() => setOpenStageId(expanded ? null : id)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left"
          data-testid={`plan-stage-${s.stageKey}-${s.status}`}>
          {expanded ? <ChevronDown size={15} className="text-slate-400 shrink-0" /> : <ChevronRight size={15} className="text-slate-400 shrink-0" />}
          <span className={`text-sm font-semibold truncate ${isSkipped ? "text-slate-400 line-through" : "text-slate-900"}`}>{s.stageLabel || s.stageKey}</span>
          {mode && <span className={`hidden sm:inline px-1.5 py-0.5 rounded text-[10px] font-semibold border ${mode.cls}`}>{mode.label}</span>}
          {!isDone && !isSkipped && (
            <span className="hidden md:inline text-[11px] text-slate-400">{SUBSTAGE_LABEL[s.subStage] || s.subStage}</span>
          )}
          <span className="ml-auto flex items-center gap-2 pl-2">
            {!isDone && !isSkipped && hint && (
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${HINT_TONE[hint.tone]}`}>{hint.label}</span>
            )}
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${STATUS_STYLE[s.status]}`}>{s.status.replace("_", " ")}</span>
            {!isSkipped && (
              <span className="hidden md:flex items-center gap-1.5 w-20">
                <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <span className="block h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                </span>
                <span className="text-[10px] text-slate-400 w-7 text-right">{pct}%</span>
              </span>
            )}
          </span>
        </button>

        {expanded && (
          <div className="px-4 pb-4 pt-1 space-y-2">
            {renderCurrentStep(s)}
            {renderChecklistSection(s)}
            {renderDetailsSection(s)}
            {renderPackageBoq(s)}
          </div>
        )}
      </div>
    );
  }

  /* the active sub-stage's primary controls, surfaced inline */
  function renderCurrentStep(s) {
    const subStages = s.substageProfile?.length ? s.substageProfile : LEGACY_SUB_STAGES;
    const subIdx = subStages.indexOf(s.subStage);
    const sub = s.subStage;

    if (s.status === "SKIPPED") {
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 flex items-center justify-between gap-2">
          <p className="text-xs text-amber-700">Skipped {s.skippedAt ? `on ${fmt(s.skippedAt)}` : ""} — the lock date passed without payment.</p>
          <button onClick={() => setConfirmUnskip(s)} disabled={busy}
            className="rounded-lg border border-amber-300 text-amber-600 hover:bg-amber-50 text-xs font-semibold py-1.5 px-3 inline-flex items-center gap-1 shrink-0"><RotateCcw size={12} /> Unskip</button>
        </div>
      );
    }
    if (s.status === "COMPLETED") {
      return (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
          <p className="text-xs text-emerald-700 inline-flex items-center gap-1.5">
            <CheckCircle2 size={13} /> Completed {fmt(s.achievedDate)}
            {s.completionImageUrl && (
              <button onClick={() => window.open(s.completionImageUrl, "_blank", "noopener")} className="inline-flex items-center gap-1 text-sky-600 hover:underline ml-1"><ImageIcon size={12} /> photo</button>
            )}
          </p>
        </div>
      );
    }
    if (s.executionMode === "DEFERRED" && !s.executionStartDate) {
      return (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs text-slate-500">{s.deferredUntil ? `Deferred until ${fmt(s.deferredUntil)}.` : "Awaiting a possession date — set the Handover WBS target or the unit's handover date."}</p>
        </div>
      );
    }

    const advanceDisabled = busy || (subIdx === 0 && !s.paymentUnlocked);
    const advanceTitle = subIdx === 0 && !s.paymentUnlocked ? "Procurement unlocks after the buyer's package payment is recorded" : "";

    return (
      <div className="rounded-xl border border-flamingo-200 bg-flamingo-50/40 p-3 space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wide text-flamingo-600">Current step · {SUBSTAGE_LABEL[sub] || sub}</p>

        {sub === "PLANNING" && (
          <div className="space-y-2">
            {/* Step 1 — float the tender & award every item first (this auto-fills the materials breakdown). */}
            <TenderPanel leadId={leadId} stage={s} onChanged={load} />

            {/* Step 2+ — breakdown → cost estimate → payment; unlocked only once every item is awarded. */}
            {!s.hasAwardedTender ? (
              <p className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                <Lock size={11} /> Award a vendor for every item to configure the cost breakdown &amp; collect payment.
              </p>
            ) : (
              <div className="space-y-2 border-t border-flamingo-100 pt-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">
                    {s.installmentAmount != null ? `Installment ${fmtINR(s.installmentAmount)}` : "Breakdown not set"}
                    {s.designLockDate ? ` · due ${fmt(s.designLockDate)}` : ""}
                  </span>
                  <button onClick={() => setChargesStage(s)} className="text-flamingo-600 hover:underline font-semibold inline-flex items-center gap-1">
                    <Receipt size={11} /> {s.installmentAmount == null ? "Set breakdown" : "Edit breakdown"}
                  </button>
                </div>
                {renderStageBoq(s)}
                {s.paymentUnlocked ? (
                  <p className="text-xs text-emerald-600 inline-flex items-center gap-1"><Check size={12} /> Installment received</p>
                ) : (
                  <button onClick={() => { setPayStage(s); setPayForm({ amount: s.installmentAmount ?? "", reference: "", date: todayISO() }); setReceiptHtml(null); }}
                    disabled={busy || s.installmentAmount == null || s.boqStatus !== "SENT"}
                    title={s.installmentAmount == null ? "Set the cost breakdown first" : s.boqStatus !== "SENT" ? "Share the cost estimate (BOQ) with the buyer first" : ""}
                    className="rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold py-2 px-3 inline-flex items-center gap-1">
                    {(s.installmentAmount == null || s.boqStatus !== "SENT") && <Lock size={11} />} Record installment
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {sub === "PROCUREMENT" && (
          <>
            {/* Tender is awarded in PLANNING; here it's a read-only reference above the PO controls. */}
            <PurchaseOrderPanel stage={s} boqGroup={packageBoq[s.stageKey]} autoOpen embedded />
            <TenderPanel leadId={leadId} stage={s} onChanged={load} />
          </>
        )}

        {sub === "CLOSURE" && (
          <button onClick={() => { setAchieveStage(s); setAchieveForm({ date: todayISO(), file: null }); }} disabled={busy}
            className="rounded-lg bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-xs font-semibold py-2 px-3 inline-flex items-center gap-1.5"><Calendar size={12} /> Mark complete (date + photo)</button>
        )}

        {/* the current sub-stage's gating tasks (mandatory ones must be done to advance) */}
        {(s.tasks || []).some((t) => t.subStage === sub) && (
          <StageTasksChecklist tasks={s.tasks} currentSubStage={s.subStage} onlySubStage={sub} onChanged={load} disabled={busy} />
        )}

        {/* one-click progression; CLOSURE finishes via Mark complete above */}
        {sub !== "CLOSURE" && (
          <div className="flex justify-end">
            <button onClick={() => doAdvance(s)} disabled={advanceDisabled} title={advanceTitle}
              className="rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white text-xs font-semibold py-1.5 px-3 inline-flex items-center gap-1">
              {subIdx === 0 && !s.paymentUnlocked && <Lock size={11} />}
              Advance to {SUBSTAGE_LABEL[subStages[subIdx + 1]] || "next"}
            </button>
          </div>
        )}
      </div>
    );
  }

  /* full Phase-Gate checklist (all sub-stages), collapsed */
  function renderChecklistSection(s) {
    const total = s.tasks?.length || 0;
    if (!total) return null;
    const done = s.tasks.filter((t) => t.status === "DONE" || t.status === "SKIPPED").length;
    const key = `${s.idExecutionPlanStageId}:checklist`;
    const isOpen = openSections.has(key);
    return (
      <div className="border-t border-slate-100 pt-2">
        <button onClick={() => toggleSection(key)} className="w-full flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
          {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <ListChecks size={13} /> Full checklist ({done}/{total})
        </button>
        {isOpen && (
          <StageTasksChecklist tasks={s.tasks} currentSubStage={s.subStage} onChanged={load} disabled={busy || s.status === "COMPLETED"} />
        )}
      </div>
    );
  }

  /* schedule / mode / breakdown details, collapsed */
  function renderDetailsSection(s) {
    const key = `${s.idExecutionPlanStageId}:details`;
    const isOpen = openSections.has(key);
    const subStages = s.substageProfile?.length ? s.substageProfile : LEGACY_SUB_STAGES;
    const subIdx = subStages.indexOf(s.subStage);
    const isDone = s.status === "COMPLETED";
    const isSplit = s.isPostPossession && (s.manufacturingStartDate || s.installationStartDate);
    return (
      <div className="border-t border-slate-100 pt-2">
        <button onClick={() => toggleSection(key)} className="w-full flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700">
          {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <Info size={13} /> Details
        </button>
        {isOpen && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-1">
              {subStages.map((ss, i) => (
                <div key={ss} className="flex items-center gap-1 flex-1">
                  <div className={`flex-1 h-1.5 rounded-full ${i < subIdx || isDone ? "bg-emerald-400" : i === subIdx ? "bg-flamingo-500" : "bg-slate-200"}`} />
                  <span className={`text-[9px] uppercase ${i === subIdx && !isDone ? "text-flamingo-600 font-bold" : "text-slate-400"}`}>{ss}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
              {s.complexityUsed && <span>Complexity: <span className="text-slate-700 font-medium">{s.complexityUsed}</span></span>}
              {isSplit ? (
                <>
                  {s.manufacturingStartDate && <span>Mfg {fmt(s.manufacturingStartDate)} → {fmt(s.manufacturingFinishDate)}</span>}
                  {s.installationStartDate && <span>Install {fmt(s.installationStartDate)} → {fmt(s.installationFinishDate)}</span>}
                </>
              ) : (
                <span>Exec {fmt(s.executionStartDate)} → {fmt(s.executionFinishDate)}</span>
              )}
              {s.procurementStartDate && <span>Procure from {fmt(s.procurementStartDate)}</span>}
            </div>
            {(s.charges?.length || 0) > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {s.charges.map((c, i) => (
                  <span key={i} className="text-[11px] bg-slate-50 border border-slate-200 text-slate-600 rounded-md px-2 py-0.5">
                    {chargeLabel(c.chargeType)} {fmtINR(c.amount)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  /* per-package approved-BOQ panel (lazy-rendered from the prefetched map) */
  function renderPackageBoq(s) {
    const grp = packageBoq[s.stageKey];
    if (!grp || !grp.lineCount) return null;
    const isOpen = openBoq.has(s.stageKey);
    return (
      <div className="mt-3 border-t border-slate-100 pt-2">
        <button
          onClick={() => toggleBoq(s.stageKey)}
          className="w-full flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-700"
        >
          {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <Boxes size={13} /> BOQ — {grp.lineCount} {grp.lineCount === 1 ? "item" : "items"}
          {grp.totalPrice != null && <span className="text-slate-400 font-normal">· {fmtINR(grp.totalPrice)}</span>}
        </button>
        {isOpen && (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-slate-400 uppercase tracking-wide">
                  <th className="py-1 pr-3">Room</th>
                  <th className="py-1 pr-3">Work item</th>
                  <th className="py-1 pr-3">Material</th>
                  <th className="py-1 pr-3 text-right">Qty</th>
                  <th className="py-1 pr-3">Unit</th>
                  <th className="py-1 text-right">Total ₹</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {grp.lines.map((l, i) => (
                  <tr key={i} className="text-slate-600">
                    <td className="py-1 pr-3">{l.room || "—"}</td>
                    <td className="py-1 pr-3">{l.workItem || "—"}</td>
                    <td className="py-1 pr-3">{l.material || "—"}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{l.quantity ?? "—"}</td>
                    <td className="py-1 pr-3">{l.unit || "—"}</td>
                    <td className="py-1 text-right tabular-nums">{l.itemTotalPrice != null ? fmtINR(l.itemTotalPrice) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
};

export default ExecutionPlanPanel;
